import React from "react";

import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

import { useTranslation } from "react-i18next";

import {
  House,
  Landmark,
  Building2,
  ShieldCheck,
  Search,
  ChevronDown,
  Loader2,
  Users,
  BadgeCheck,
  MailWarning,
  Database,
  RefreshCw,
  Check,
  Copy,
} from "lucide-react";

import { getAdminUsers } from "./adminUsersApi";

import "./AdminUsersPage.css";

function useAnimatedNumber(targetValue, duration = 650) {
  const [value, setValue] = React.useState(0);

  React.useEffect(() => {
    const target = Number(targetValue) || 0;

    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setValue(target);

      return undefined;
    }

    let frame;

    let start = null;

    function animate(timestamp) {
      if (!start) start = timestamp;

      const progress = Math.min((timestamp - start) / duration, 1);

      const eased = 1 - Math.pow(1 - progress, 3);

      setValue(target * eased);

      if (progress < 1) {
        frame = requestAnimationFrame(animate);
      }
    }

    frame = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(frame);
  }, [targetValue, duration]);

  return Math.round(value);
}

function CopyableListEmail({
  value,
  copyLabel = "Copy email address",
  copiedLabel = "Copied",
  onInteract,
}) {
  const [copied, setCopied] = React.useState(false);

  const timerRef = React.useRef(null);

  React.useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  async function handleCopy(event) {
    event.preventDefault();
    event.stopPropagation();

    onInteract?.();

    if (!value) return;

    try {
      await navigator.clipboard.writeText(String(value));
    } catch {
      const textarea = document.createElement("textarea");

      textarea.value = String(value);
      textarea.setAttribute("readonly", "");

      textarea.style.position = "fixed";
      textarea.style.opacity = "0";

      document.body.appendChild(textarea);

      textarea.select();

      document.execCommand("copy");

      document.body.removeChild(textarea);
    }

    setCopied(true);

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      setCopied(false);
    }, 1500);
  }

  if (!value) {
    return null;
  }

  return (
    <div className="admin-users-user__email">
      <span>{value}</span>

      <button
        type="button"
        className={[
          "admin-users-user__copy-email",
          copied ? "admin-users-user__copy-email--copied" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={handleCopy}
        onMouseDown={(event) => {
          event.stopPropagation();
        }}
        onMouseEnter={() => {
          onInteract?.();
        }}
        onMouseMove={(event) => {
          event.stopPropagation();
          onInteract?.();
        }}
        onKeyDown={(event) => {
          event.stopPropagation();
        }}
        aria-label={copied ? copiedLabel : copyLabel}
        title={copied ? copiedLabel : copyLabel}
      >
        {copied ? (
          <Check size={13} strokeWidth={2.4} />
        ) : (
          <Copy size={13} strokeWidth={2} />
        )}
      </button>
    </div>
  );
}

function getAuthenticationMeta(user, t) {
  const status = user?.authentication?.status;

  if (status === "VERIFIED") {
    return {
      key: "VERIFIED",

      label: t("authentication.verified", "Verified"),

      icon: BadgeCheck,
    };
  }

  if (status === "UNVERIFIED") {
    return {
      key: "UNVERIFIED",

      label: t("authentication.unverified", "Unverified"),

      icon: MailWarning,
    };
  }

  return {
    key: "LOCAL_ONLY",

    label: t("authentication.localOnly", "Local only"),

    icon: Database,
  };
}

export default function AdminUsersPage() {
  const navigate = useNavigate();

  const { getAccessTokenSilently } = useAuth0();

  const { t } = useTranslation("admin-users");

  const VALID_CATEGORIES = ["HOMEOWNER", "LENDER", "OSS", "INVESTOR"];

  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get("category");
  const category = VALID_CATEGORIES.includes(categoryParam)
    ? categoryParam
    : "HOMEOWNER";

  const [search, setSearch] = React.useState("");

  const [debouncedSearch, setDebouncedSearch] = React.useState("");

  const [region, setRegion] = React.useState("ALL");

  const [authStatus, setAuthStatus] = React.useState("ALL");

  const [ordering, setOrdering] = React.useState("RECENTLY_UPDATED");

  const [page, setPage] = React.useState(1);

  const [data, setData] = React.useState(null);

  const [initialLoading, setInitialLoading] = React.useState(true);

  const [refreshing, setRefreshing] = React.useState(false);

  const [error, setError] = React.useState("");

  const [refreshKey, setRefreshKey] = React.useState(0);

  const requestIdRef = React.useRef(0);

  const [userTooltip, setUserTooltip] = React.useState({
    visible: false,

    userId: null,

    x: 0,

    y: 0,
  });

  function hideUserTooltip() {
    setUserTooltip({
      visible: false,

      userId: null,

      x: 0,

      y: 0,
    });
  }

  React.useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(search);

      setPage(1);
    }, 250);

    return () => window.clearTimeout(timeout);
  }, [search]);

  React.useEffect(() => {
    const controller = new AbortController();

    const requestId = ++requestIdRef.current;

    async function loadUsers() {
      try {
        if (!data) {
          setInitialLoading(true);
        } else {
          setRefreshing(true);
        }

        setError("");

        const accessToken = await getAccessTokenSilently();

        const nextData = await getAdminUsers({
          accessToken,

          category,

          search: debouncedSearch,

          region,

          authStatus,

          ordering,

          page,

          signal: controller.signal,
        });

        if (requestId === requestIdRef.current) {
          setData(nextData);
        }
      } catch (loadError) {
        if (loadError.name === "AbortError") {
          return;
        }

        console.error("Failed to load admin users:", loadError);

        if (requestId === requestIdRef.current) {
          setError(
            loadError.message || t("errors.load", "Could not load users."),
          );
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setInitialLoading(false);

          setRefreshing(false);
        }
      }
    }

    loadUsers();

    return () => controller.abort();
  }, [
    category,
    debouncedSearch,
    region,
    authStatus,
    ordering,
    page,
    refreshKey,
    getAccessTokenSilently,
    t,
  ]);

  const summary = data?.summary || {};

  const pagination = data?.pagination || {};

  const users = data?.results || [];

  const categoryCards = [
    {
      key: "HOMEOWNER",

      label: t("categories.homebuyers.title"),

      description: t("categories.homebuyers.description"),

      count: summary.homeowners || 0,

      icon: House,
    },

    {
      key: "LENDER",

      label: t("categories.lenders.title"),

      description: t("categories.lenders.description"),

      count: summary.lenders || 0,

      icon: Landmark,
    },

    {
      key: "OSS",

      label: t("categories.oss.title"),

      description: t("categories.oss.description"),

      count: summary.oss || 0,

      icon: Building2,
    },

    {
      key: "INVESTOR",

      label: t("categories.investors.title"),

      description: t("categories.investors.description"),

      count: summary.investors || 0,

      icon: ShieldCheck,
    },
  ];

  const activeCard =
    categoryCards.find((item) => item.key === category) || categoryCards[0];

  const ActiveIcon = activeCard.icon;

  const animatedTotal = useAnimatedNumber(summary.total_users || 0);

  function selectCategory(nextCategory) {
    if (nextCategory === category) {
      return;
    }

    setSearchParams({ category: nextCategory }, { replace: true });
    setPage(1);
  }

  function openUser(userId) {
    navigate(`/admin/users/${userId}`, {
      state: { fromCategory: category },
    });
  }

  function handleRegionChange(event) {
    setRegion(event.target.value);

    setPage(1);
  }

  function handleAuthStatusChange(event) {
    setAuthStatus(event.target.value);

    setPage(1);
  }

  function handleOrderingChange(event) {
    setOrdering(event.target.value);

    setPage(1);
  }

  function handleRefresh() {
    hideUserTooltip();
    setRefreshKey((current) => current + 1);
  }

  if (initialLoading && !data) {
    return (
      <div className="admin-users">
        <div className="admin-users__skeleton">
          <div className="admin-users__skeleton-header" />

          <div className="admin-users__skeleton-cards">
            {[1, 2, 3, 4].map((item) => (
              <div key={item} className="admin-users__skeleton-card" />
            ))}
          </div>

          <div className="admin-users__skeleton-list" />
        </div>
      </div>
    );
  }

  return (
    <div className="admin-users">
      <header className="admin-users__header">
        <div className="admin-users__header-copy">
          <h1>{t("header.title")}</h1>

          <p>{t("header.subtitle")}</p>
        </div>

        <div className="admin-users__header-actions">
          <button
            type="button"
            className="admin-users__refresh"
            onClick={handleRefresh}
            disabled={refreshing}
            aria-label={t("actions.refresh", "Refresh users")}
            title={t("actions.refresh", "Refresh users")}
          >
            <RefreshCw
              size={18}
              strokeWidth={2}
              className={refreshing ? "is-spinning" : ""}
            />

            <span>
              {refreshing
                ? t("actions.refreshing", "Refreshing...")
                : t("actions.refresh", "Refresh")}
            </span>
          </button>

          <div className="admin-users__total">
            <span>{t("header.total")}</span>

            <strong>{animatedTotal}</strong>
          </div>
        </div>
      </header>

      <section className="admin-users__categories">
        {categoryCards.map((item, index) => {
          const Icon = item.icon;

          return (
            <button
              key={item.key}
              type="button"
              data-hover={t("categories.hover")}
              className={`admin-users-category ${
                category === item.key ? "is-active" : ""
              }`}
              style={{
                "--entry-delay": `${index * 55}ms`,
              }}
              onClick={() => selectCategory(item.key)}
            >
              <div className="admin-users-category__top">
                <span className="admin-users-category__icon">
                  <Icon size={20} strokeWidth={2} />
                </span>

                <strong className="admin-users-category__count">
                  {item.count}
                </strong>
              </div>

              <h2>{item.label}</h2>

              <p>{item.description}</p>
            </button>
          );
        })}
      </section>

      <section className="admin-users__filters">
        <div className="admin-users-search">
          <Search size={18} className="admin-users-search__icon" />

          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("search.placeholder")}
          />

          {refreshing ? (
            <Loader2 size={17} className="admin-users-search__spinner" />
          ) : null}
        </div>

        <div className="admin-users-select">
          <select
            value={region}
            onChange={handleRegionChange}
            aria-label={t("filters.regionLabel")}
          >
            <option value="ALL">{t("filters.regions.all")}</option>

            <option value="FLANDERS">{t("filters.regions.flanders")}</option>

            <option value="GIRONA">{t("filters.regions.girona")}</option>
          </select>

          <ChevronDown size={15} />
        </div>

        <div className="admin-users-select admin-users-select--auth">
          <select
            value={authStatus}
            onChange={handleAuthStatusChange}
            aria-label={t(
              "filters.authenticationLabel",

              "Authentication status",
            )}
          >
            <option value="ALL">
              {t("filters.authentication.all", "All accounts")}
            </option>

            <option value="VERIFIED">
              {t("filters.authentication.verified", "Verified")}
            </option>

            <option value="UNVERIFIED">
              {t("filters.authentication.unverified", "Unverified")}
            </option>

            <option value="LOCAL_ONLY">
              {t("filters.authentication.localOnly", "Local only")}
            </option>
          </select>

          <ChevronDown size={15} />
        </div>

        <div className="admin-users-select admin-users-select--ordering">
          <select
            value={ordering}
            onChange={handleOrderingChange}
            aria-label={t("filters.orderingLabel")}
          >
            <option value="RECENTLY_UPDATED">
              {t("filters.ordering.updated")}
            </option>

            <option value="RECENTLY_REGISTERED">
              {t("filters.ordering.registered")}
            </option>

            <option value="NAME_ASC">{t("filters.ordering.nameAsc")}</option>

            <option value="NAME_DESC">{t("filters.ordering.nameDesc")}</option>
          </select>

          <ChevronDown size={15} />
        </div>
      </section>

      <section
        className={`admin-users-list ${refreshing ? "is-refreshing" : ""}`}
      >
        <div className="admin-users-list__header">
          <div className="admin-users-list__title">
            <span className="admin-users-list__icon">
              <ActiveIcon size={18} />
            </span>

            <h2>{activeCard.label}</h2>
          </div>

          <span className="admin-users-list__range">
            {pagination.total_items > 0
              ? t("list.showing", {
                  from: (pagination.page - 1) * pagination.page_size + 1,

                  to: Math.min(
                    pagination.page * pagination.page_size,

                    pagination.total_items,
                  ),

                  total: pagination.total_items,
                })
              : t("list.none")}
          </span>
        </div>

        <div className="admin-users-list__body">
          {error ? (
            <div className="admin-users-list__empty">
              <strong>{t("errors.title")}</strong>

              <span>{error}</span>
            </div>
          ) : users.length === 0 ? (
            <div className="admin-users-list__empty">
              <Users size={24} />

              <strong>{t("list.emptyTitle")}</strong>

              <span>{t("list.emptyText")}</span>
            </div>
          ) : (
            users.map((user, index) => {
              const authMeta = getAuthenticationMeta(user, t);

              const AuthIcon = authMeta.icon;

              return (
                <article
                  key={user.id}
                  className="admin-users-user"
                  style={{
                    "--entry-delay": `${index * 45}ms`,
                  }}
                  role="button"
                  tabIndex={0}
                  onClick={() => openUser(user.id)}
                  onMouseMove={(event) => {
                    if (event.target.closest(".admin-users-user__actions")) {
                      hideUserTooltip();

                      return;
                    }

                    setUserTooltip({
                      visible: true,

                      userId: user.id,

                      x: event.clientX,

                      y: event.clientY,
                    });
                  }}
                  onMouseLeave={hideUserTooltip}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();

                      openUser(user.id);
                    }
                  }}
                >
                  <div
                    className={`admin-users-user__avatar ${
                      user.avatar_url ? "admin-users-user__avatar--image" : ""
                    }`}
                  >
                    {user.avatar_url ? (
                      <img
                        src={user.avatar_url}
                        alt={user.display_name}
                        className="admin-users-user__avatar-image"
                      />
                    ) : (
                      <span>{user.initials}</span>
                    )}
                  </div>

                  <div className="admin-users-user__details">
                    <div className="admin-users-user__name-line">
                      <h3>{user.display_name}</h3>

                      <span
                        className={`admin-users-auth-badge admin-users-auth-badge--${authMeta.key.toLowerCase()}`}
                      >
                        <AuthIcon size={12} />

                        {authMeta.label}
                      </span>

                      <span
                        className={`admin-users-account-badge ${
                          user.is_active
                            ? "admin-users-account-badge--active"
                            : "admin-users-account-badge--inactive"
                        }`}
                      >
                        {user.is_active
                          ? t("accountStatus.active", "Active")
                          : t("accountStatus.inactive", "Inactive")}
                      </span>
                    </div>

                    <CopyableListEmail
                      value={user.email}
                      copyLabel={t("actions.copyEmail", "Copy email address")}
                      copiedLabel={t("actions.emailCopied", "Copied")}
                      onInteract={hideUserTooltip}
                    />

                    <span>
                      {user.country_code
                        ? t(`countries.${user.country_code}`, user.country_code)
                        : t("common.notSpecified")}

                      {user.region
                        ? ` / ${t(`regions.${user.region}`, user.region)}`
                        : ""}
                    </span>

                    {user.organisation_name ? (
                      <span>
                        {t("list.organisation", {
                          organisation: user.organisation_name,
                        })}
                      </span>
                    ) : null}
                  </div>

                  <div className="admin-users-user__actions">
                    <button
                      type="button"
                      className="admin-users-btn admin-users-btn--primary"
                      onMouseEnter={hideUserTooltip}
                      onMouseMove={(event) => {
                        event.stopPropagation();

                        hideUserTooltip();
                      }}
                      onClick={(event) => {
                        event.stopPropagation();

                        hideUserTooltip();

                        openUser(user.id);
                      }}
                    >
                      {t("actions.openProfile")}
                    </button>
                  </div>
                </article>
              );
            })
          )}
        </div>

        {pagination.total_pages > 1 ? (
          <div className="admin-users-pagination">
            <span>
              {t("pagination.summary", {
                from: (pagination.page - 1) * pagination.page_size + 1,

                to: Math.min(
                  pagination.page * pagination.page_size,

                  pagination.total_items,
                ),

                total: pagination.total_items,

                category: activeCard.label.toLowerCase(),
              })}
            </span>

            <div className="admin-users-pagination__buttons">
              {Array.from(
                {
                  length: pagination.total_pages,
                },

                (_, index) => index + 1,
              )

                .slice(Math.max(page - 2, 0), Math.max(page - 2, 0) + 3)

                .map((pageNumber) => (
                  <button
                    key={pageNumber}
                    type="button"
                    className={
                      pageNumber === pagination.page ? "is-active" : ""
                    }
                    onClick={() => setPage(pageNumber)}
                  >
                    {pageNumber}
                  </button>
                ))}

              {pagination.total_pages > 3 ? <span>…</span> : null}
            </div>
          </div>
        ) : null}
      </section>

      {userTooltip.visible
        ? createPortal(
            <span
              className="admin-users-user__floating-hint"
              style={{
                left: userTooltip.x + 14,

                top: userTooltip.y + 14,
              }}
            >
              {t("list.hoverProfile")}
            </span>,

            document.body,
          )
        : null}
    </div>
  );
}
